import React from "react";

import { Command } from "./command";
import { Node, createElement } from "./tree";
import { Block, StyleMap } from "./types";

/**
 * Resolves a style map entry to element type and props
 */
function resolveStyleOptions(
    styleMap: StyleMap,
    style: string,
    fallback: string = "span",
): {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    element: string | React.ComponentType<any>;
    props: Record<string, unknown>;
} {
    const config = styleMap[style];

    if (!config) {
        return { element: fallback, props: {} };
    }

    if (typeof config === "string" || typeof config === "function") {
        return { element: config, props: {} };
    }

    // config is { element: keyof JSX.IntrinsicElements; props?: React.HTMLAttributes<HTMLElement> }
    return {
        element: config.element,
        props: (config.props ?? {}) as Record<string, unknown>,
    };
}

/**
 * StyleState tracks currently active inline styles and wraps nodes with style elements.
 */
export class StyleState {
    private styles: string[] = [];

    constructor(private styleMap: StyleMap) {}

    /**
     * Apply a command to update the active styles list
     */
    apply(command: Command): void {
        switch (command.name) {
            case "start_inline_style":
                this.styles.push(command.data);
                break;
            case "stop_inline_style": {
                const index = this.styles.indexOf(command.data);
                if (index !== -1) {
                    this.styles.splice(index, 1);
                }
                break;
            }
        }
    }

    /**
     * Check if there are any active styles
     */
    isEmpty(): boolean {
        return this.styles.length === 0;
    }

    /**
     * Wrap a node with all currently active style elements.
     * Styles are sorted and nested from outside-in.
     */
    renderStyles(
        decoratedNode: Node,
        block: Block<string, string>,
        blocks: Block<string, string>[],
    ): Node {
        if (this.isEmpty()) {
            return decoratedNode;
        }

        // Sort in reverse order for consistent nesting (matches Python implementation)
        const sortedStyles = [...this.styles].sort().reverse();

        let node = decoratedNode;
        for (const style of sortedStyles) {
            const { element, props } = resolveStyleOptions(
                this.styleMap,
                style,
            );
            node = createElement(
                element,
                {
                    ...props,
                    block,
                    blocks,
                    inlineStyleRange: { style },
                },
                node,
            );
        }

        return node;
    }
}
